import { useEffect, useState } from 'react';
import { useSession } from './lib/session';

/**
 * A short, quiet intro for signed-out visitors, in the manner of Braytech's loading screen: the Z, the name, a small
 * "loading" line with a thin bar, a soft glow from below; no buttons and nothing to answer (signing in is offered in
 * the page itself). It is only a layer on top: the page is already drawn underneath (and the worker's text is in the
 * HTML), so it never holds the page up or hides it from search engines. Once a visit (sessionStorage), never when
 * signed in; it goes once the page has loaded (at least about a second, at most two), or at once on a click or Esc.
 * With reduced motion nothing moves; it only fades.
 */
const SEEN_KEY = 'd2c.splash';
const seen = () => { try { return sessionStorage.getItem(SEEN_KEY) === '1'; } catch { return true; } };   // storage blocked: do not show it on every page
const markSeen = () => { try { sessionStorage.setItem(SEEN_KEY, '1'); } catch { /* storage blocked */ } };
const MIN = 1100, MAX = 2000;

export default function Splash() {
  const session = useSession();
  const [phase, setPhase] = useState<'off' | 'on' | 'out'>('off');

  useEffect(() => {
    if (session.status !== 'signedOut' || phase !== 'off' || seen()) return;
    markSeen();
    setPhase('on');
  }, [session.status]);   // eslint-disable-line react-hooks/exhaustive-deps

  // goes once the page has loaded (not before MIN, not after MAX); a click or Esc sends it at once
  useEffect(() => {
    if (phase !== 'on') return;
    const start = performance.now();
    const go = () => setPhase('out');
    const loaded = () => window.setTimeout(go, Math.max(0, MIN - (performance.now() - start)));
    let early: number | undefined;
    if (document.readyState === 'complete') early = loaded(); else window.addEventListener('load', loaded, { once: true });
    const cap = window.setTimeout(go, MAX);
    const key = (e: KeyboardEvent) => { if (e.key === 'Escape') go(); };
    window.addEventListener('keydown', key);
    return () => { window.clearTimeout(early); window.clearTimeout(cap); window.removeEventListener('load', loaded); window.removeEventListener('keydown', key); };
  }, [phase]);
  // gone once faded (the fade's own end usually comes first)
  useEffect(() => {
    if (phase !== 'out') return;
    const t = setTimeout(() => setPhase('off'), 700);
    return () => clearTimeout(t);
  }, [phase]);

  if (phase === 'off') return null;
  return (
    <div className={`splash ${phase}`} onClick={() => setPhase('out')} onTransitionEnd={(e) => { if (e.target === e.currentTarget && phase === 'out') setPhase('off'); }}
      aria-hidden="true">
      <div className="splash-corner">D2 Companion<small>d2companion.co.za</small></div>
      <div className="splash-mid">
        <img src="/logo.png" alt="" width="72" height="72" className="splash-logo" />
        <div className="splash-name">D2 Companion</div>
        <div className="splash-status"><i className="splash-spin" />Reading the Destiny database</div>
        <div className="splash-bar"><i style={{ animationDuration: `${MIN}ms` }} /></div>
      </div>
    </div>
  );
}
